'use client'

import { useEffect, useRef, useState } from 'react'

import { selectIsSelectTargetBoardToUploadModalOpen } from 'features/common/model/selectors/appSelectors'
import { actions, setIsAddNewBoardModalOpen, setIsSelectTargetBoardToUploadModalOpen } from 'features/common/model/slice/appSlice'
import { checkFilesForRenameThunk, uploadFilesThunk } from 'features/files-uploading/model/slice/filesUploadingSlice'
import { getUserRole } from 'features/user/model/selectors/userSelectors'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { Button, Flex, Modal, PlusCircleOutlined, CaretDownFilled } from 'shared/ui'
import { NodeTypes } from 'shared/constants/common'
import { getTreeLikeSelectorFromNodes } from 'shared/utils/filesUtils'
import styled from 'styled-components'
import { hasAccess } from 'shared/utils/access'
import { MemberRole } from 'features/team/model/types/members'
import { currentBoardIdSelector } from 'features/boards/model/selectors/boardsSelectors'

import { getDuplicateModal } from '../../files-uploading/selectors/filesUploadingSelectors'
import { boardsActions } from '../model/slice/boardsSlice'

type TreeNode = {
  key: string
  title: string
  children?: TreeNode[]
}

type Board = {
  id: string | number
  parentId?: string | number | null
  name?: string
}

// Static variable to hold files between component instances
let pendingUploadFiles: FileList | null = null

export const setUploadFilesForTargetBoardModal = (files: FileList) => {
  pendingUploadFiles = files
  return files
}

function getAncestorKeys(nodes: TreeNode[], targetKey: string): string[] {
  const stack: Array<{ node: TreeNode; path: string[] }> = []
  for (const n of nodes) stack.push({ node: n, path: [] })

  while (stack.length) {
    const { node, path } = stack.pop()!
    const key = String(node.key)
    if (key === targetKey) return path
    if (node.children && node.children.length) {
      for (const child of node.children) {
        stack.push({ node: child, path: [...path, key] })
      }
    }
  }
  return []
}

export const SelectTargetBoardToUploadModal = ({ boards }: { boards: Board[] }) => {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(selectIsSelectTargetBoardToUploadModalOpen)
  const currentBoardId = useAppSelector(currentBoardIdSelector)

  const [selectedBoardId, setSelectedBoardId] = useState<string>('')
  const [uploadFiles, setUploadFiles] = useState<FileList | null>(null)
  const duplicateModal = useAppSelector(getDuplicateModal)
  const userRole = useAppSelector(getUserRole)

  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const intendedParentIdRef = useRef<string | null>(null)
  const prevBoardIdsRef = useRef<Set<string>>(new Set())

  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const safeBoards = Array.isArray(boards) ? boards : []

  useEffect(() => {
    const queue = duplicateModal.queue
    const decisions = duplicateModal.decisions
    if (queue.length > 0 && queue.length === Object.keys(decisions).length) {
      if (uploadFiles && uploadFiles.length > 0) {
        dispatch(uploadFilesThunk({
          files: uploadFiles,
          symlinkParentId: selectedBoardId || undefined,
          modificators: []
        }))
        handleClose()
      }
    }
  }, [duplicateModal, uploadFiles])

  useEffect(() => {
    if (isOpen && pendingUploadFiles) {
      setUploadFiles(pendingUploadFiles)
    }
    if (isOpen) {
      prevBoardIdsRef.current = new Set(safeBoards.map(b => String(b.id)))
    }
  }, [isOpen])

  const handleClose = () => {
    dispatch(setIsSelectTargetBoardToUploadModalOpen(false))
    setSelectedBoardId('')
    setUploadFiles(null)
    pendingUploadFiles = null
    intendedParentIdRef.current = null
  }

  const handleSubmit = () => {
    dispatch(checkFilesForRenameThunk({
      files: uploadFiles,
      parentId: selectedBoardId || undefined,
      type: NodeTypes.FILE,
    })).unwrap().then((duplicates) => {
      if (!duplicates) return
      if (duplicates.flat().length > 0) return

      dispatch(uploadFilesThunk({
        files: uploadFiles,
        symlinkParentId: selectedBoardId || undefined,
        modificators: []
      }))
      handleClose()
    })
  }

  const handleAddBoardRoot = () => {
    intendedParentIdRef.current = null
    dispatch(actions.setModalBoardData({ boardIds: null, boards: null, parentId: null }))
    dispatch(setIsAddNewBoardModalOpen(true))
    dispatch(boardsActions.setSelectedParentId(null))
  }

  const treeData = getTreeLikeSelectorFromNodes(boards, [])

  const toggleExpand = (key: string) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const renderNode = (node: TreeNode, level = 1) => {
    const key = String(node.key)
    const hasChildren = !!node.children && node.children.length > 0
    const isExpanded = !!expanded[key]
    const isSelected = selectedBoardId === key

    return (
      <div key={key}>
        <SItem
          ref={(el) => { itemRefs.current[key] = el }}
          level={level}
          className={isSelected ? 'active-item' : ''}
          onClick={() => setSelectedBoardId(key)}
        >
          <div className="left">
            {hasChildren ? (
              <div
                className="expand-icon"
                onClick={(e) => { e.stopPropagation(); toggleExpand(key) }}
              >
                <CaretDownFilled className={isExpanded ? 'icon-normal' : 'icon-rotate'} />
              </div>
            ) : (
              <div className="expand-icon-placeholder" />
            )}

            <span className="label"
              title={node.title}
            >
              {node.title}
            </span>
          </div>

          <div className="actions">
            {hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && (
              <PlusCircleOutlined
                className="action-icon"
                onClick={(e) => {
                  e.stopPropagation()
                  intendedParentIdRef.current = key
                  dispatch(actions.setModalBoardData({ boardIds: null, boards: null, parentId: key }))
                  dispatch(setIsAddNewBoardModalOpen(true))
                  dispatch(boardsActions.setSelectedParentId(key))
                }}
              />
            )}
          </div>
        </SItem>

        {hasChildren && (
          <SChildren className={isExpanded ? 'expanded' : ''}>
            {node.children!.map(child => renderNode(child, level + 1))}
          </SChildren>
        )}
      </div>
    )
  }

  useEffect(() => {
    if (!isOpen) return
    const prev = prevBoardIdsRef.current
    const currentIds = new Set(safeBoards.map(b => String(b.id)))

    const added = safeBoards.filter(b => !prev.has(String(b.id)))
    if (added.length > 0) {
      const intendedParent = intendedParentIdRef.current
      const created = added.find(b => {
        const pid = b.parentId == null ? null : String(b.parentId)
        return pid === intendedParent
      }) || added[0]

      if (created) {
        const newId = String(created.id)
        setSelectedBoardId(newId)

        const ancestorKeys = getAncestorKeys(treeData, newId)
        if (ancestorKeys.length) {
          setExpanded(prevExp => {
            const next = { ...prevExp }
            for (const k of ancestorKeys) next[k] = true
            return next
          })
        }

        intendedParentIdRef.current = null
      }
    }

    prevBoardIdsRef.current = currentIds
  }, [safeBoards, isOpen, treeData])

  useEffect(() => {
    if (!isOpen || !currentBoardId || !safeBoards.length || selectedBoardId) return

    const targetId = String(currentBoardId)
    const exists = safeBoards.some(b => String(b.id) === targetId)

    if (!exists) return

    setSelectedBoardId(targetId)

    const ancestorKeys = getAncestorKeys(treeData, targetId)

    if (ancestorKeys.length) {
      setExpanded(prevExp => {
        const next = { ...prevExp }
        for (const k of ancestorKeys) next[k] = true
        return next
      })
    }
  }, [isOpen, currentBoardId, safeBoards, treeData, selectedBoardId])

  useEffect(() => {
    if (selectedBoardId && itemRefs.current[selectedBoardId]) {
      itemRefs.current[selectedBoardId]!.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      })
    }
  }, [selectedBoardId])

  return (
    <Modal
      title="Select Target Board"
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
    >
      <SContent>
        <SModalDescription>
          Select a destination board to upload your files to.
        </SModalDescription>

        <SListContainer>
          {treeData.map(n => renderNode(n, 1))}
        </SListContainer>

        <Flex justify='space-between'>
          {hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && (
            <SAddNewBoardButton onClick={handleAddBoardRoot}>
              <PlusCircleOutlined />
              Create Board
            </SAddNewBoardButton>
          )}

          <SButtonsContainer>
            <Button size='large'
              onClick={handleClose}
            >
              Cancel
            </Button>

            <Button
              type="primary"
              size='large'
              onClick={handleSubmit}
              disabled={!uploadFiles || !selectedBoardId}
            >
              Upload
            </Button>
          </SButtonsContainer>
        </Flex>
      </SContent>
    </Modal>
  )
}

const SContent = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const SListContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  min-height: 240px;
  max-height: 472px;
  overflow: auto;
  padding: 2px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const SChildren = styled.div`
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.2s ease-in;

  &.expanded {
    max-height: 1000px;
    overflow: visible;
  }
`

interface SItemProps { level: number }
const SItem = styled.div<SItemProps>`
  min-height: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 16px 0px ${({ level }) => 24 * level}px;
  cursor: pointer;
  transition: all 0.3s;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.88);
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  border-radius: 8px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }

  &.active-item {
    background-color: rgba(0, 0, 0, 0.06);
  }

  .left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    overflow: hidden;
  }

  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actions {
    display: none;
    gap: 6px;
    flex-shrink: 0;
  }

  &:hover .actions {
    display: flex;
  }

  .action-icon {
    font-size: 18px;
    color: rgba(0, 0, 0, 0.45);
    transition: color 0.2s ease;
  }
  .action-icon:hover {
    color: var(--color-primary);
  }

  .expand-icon, .expand-icon-placeholder {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(0,0,0,0.88);
    flex-shrink: 0;
  }
  .expand-icon-placeholder { pointer-events: none; }

  .icon-rotate { transition: transform 0.2s; transform: rotate(-90deg); }
  .icon-normal { transition: transform 0.2s; transform: rotate(0); }
`

const SButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`

const SAddNewBoardButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`

const SModalDescription = styled.div`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
`
